/**
 * Lea Chatbot Block
 *
 * Injection block that loads the Lea AI chatbot widget. The vendor snippet
 * pasted on the property looks like:
 *
 *   <script src="https://ai-chat-frontend.lea.ai/api/embed?pid=c258...77cc"></script>
 *
 * The only per-property value is the `pid` query param — a long hex property
 * id. The embed endpoint reads it and paints the chat widget itself, so
 * rather than re-injecting whatever blob is pasted we extract the pid and
 * build a canonical embed script. The field accepts the full snippet, the
 * embed URL, or the bare pid, so the ops team can paste whichever they have
 * on hand.
 *
 * Why singleton + never-tear-down: Lea paints its launcher directly into
 * <body> (outside our tracking) and exposes no teardown/destroy API, so
 * re-running the embed duplicates the widget. We inject once and leave it
 * alone for the tab's lifetime, which survives StrictMode and SPA navigation
 * remounts. Subsequent edits to the pid won't reflect until a hard refresh —
 * an acceptable trade for not duplicating the widget. This mirrors the
 * betterbot-chatbot / entrata-chatbot / anyonehome-chatbot blocks.
 */

import { useEffect } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import type { BlockDefinition, BlockProps } from '../../types';

const INJECTED_NODE_ATTRIBUTE = 'data-lea-chatbot-node';

// Pull the pid out of an embed URL or full snippet, e.g.
// `https://ai-chat-frontend.lea.ai/api/embed?pid=c258...` -> `c258...`.
const PID_PARAM_PATTERN = /[?&]pid=([a-f0-9]+)/i;
// Markers that mean we were handed a snippet or URL (so a missing pid is a
// real failure, not a bare pid to use verbatim).
const SNIPPET_PATTERN = /<script[\s>]|lea\.ai|https?:\/\//i;
// A bare value must be a plausible hex property id to build an embed URL from.
const PID_PATTERN = /^[a-f0-9]{16,}$/i;

type LeaChatbotState = {
  nodes: Node[];
  value: string | null;
};

function normalizeValue(value: string | null | undefined) {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed || null;
}

/**
 * Accept the full embed snippet, the embed URL, or a bare pid. If the value
 * contains a `pid=` query param, use the captured id. Otherwise, if it looks
 * like markup or a URL without one, treat it as unusable (null). A plain hex
 * string is assumed to be the pid itself.
 */
function extractPid(value: string | null | undefined) {
  const normalized = normalizeValue(value);
  if (!normalized) return null;

  const match = normalized.match(PID_PARAM_PATTERN);
  const pid = normalizeValue(match?.[1]);
  if (pid) return pid.toLowerCase();

  if (SNIPPET_PATTERN.test(normalized)) return null;
  return PID_PATTERN.test(normalized) ? normalized.toLowerCase() : null;
}

function createHeadNodes(pid: string) {
  // The embed endpoint reads the pid from its own URL, then appends the chat
  // widget to <body> itself — this one canonical script is the whole
  // integration.
  const embedScript = document.createElement('script');
  embedScript.src = `https://ai-chat-frontend.lea.ai/api/embed?pid=${pid}`;
  embedScript.setAttribute(INJECTED_NODE_ATTRIBUTE, 'true');

  return [embedScript];
}

function getLeaChatbotState() {
  const windowWithState = window as Window & {
    __leaChatbotState?: LeaChatbotState;
  };

  // Single tab-wide instance. Once we've injected the embed, we remember it
  // here so subsequent mounts (StrictMode, SPA navigation, etc.) skip
  // re-injection — Lea paints its widget directly in <body>, and re-running
  // the embed duplicates the widget.
  if (windowWithState.__leaChatbotState) {
    return windowWithState.__leaChatbotState;
  }

  const state: LeaChatbotState = {
    nodes: [],
    value: null,
  };

  windowWithState.__leaChatbotState = state;
  return state;
}

function LeaChatbotComponent(_props: BlockProps) {
  const pid = extractPid(
    useBlockToken('embed_script', {
      defaultField: 'custom_lea_chatbot',
    }),
  );

  useEffect(() => {
    if (!pid) return;

    const state = getLeaChatbotState();

    // Load the embed exactly once per tab and never tear it down. Lea creates
    // its launcher DOM directly in <body> — outside our tracking — and has no
    // teardown API. Tearing down + re-injecting on remount or SPA navigation
    // makes the vendor paint a SECOND widget on top of the existing one,
    // since the original body DOM isn't ours to clean up. The safe-and-simple
    // answer is to behave like a script in index.html: inject once, leave
    // alone for the tab's lifetime. Subsequent edits to the pid won't reflect
    // until a hard refresh — an acceptable trade for not duplicating the
    // widget.
    if (state.value) return;

    const nodes = createHeadNodes(pid);

    // Mark injected synchronously so a concurrent remount can't inject a
    // second embed before this one finishes.
    state.value = pid;
    state.nodes = nodes;

    nodes.forEach(node => document.head.appendChild(node));
  }, [pid]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'LeaChatbot',
  displayName: 'Lea Chatbot',
  tokens: {
    embed_script: {
      label: 'Lea Embed Script or Property ID',
      type: 'text',
      defaultField: 'custom_lea_chatbot',
      suggestedFields: ['custom_lea_chatbot'],
      description:
        'Lea embed snippet, embed URL, or bare pid (the hex id from the ?pid= query param). Full snippets are parsed for the pid.',
      required: true,
    },
  },
};

export const leaChatbotBlock: BlockDefinition = {
  id: 'lea-chatbot',
  name: 'Lea Chatbot',
  description:
    'Lea AI chatbot widget injected from the property embed snippet or pid. The floating launcher is the UI — clicking it opens the chat flow.',
  category: 'Integrations',
  placement: 'injection',
  defaultScope: 'template',
  component: LeaChatbotComponent,
  contentSchema,
  tags: ['lea', 'lea.ai', 'chatbot', 'chat', 'ai', 'leasing assistant', 'widget'],
};
